/**
 * Registro do que aconteceu com os motores locais.
 *
 * Existe por causa de um relato preciso: "estava baixando e simplesmente parou
 * de baixar sem log". O erro existia e era emitido — mas tinha para onde ir
 * apenas enquanto a tela que o esperava estivesse montada. Sair da aba, ou a
 * lista se redesenhar, e a explicação desaparecia com o elemento que a
 * mostraria. Sobrava o download parado e nenhuma pista.
 *
 * A correção não é mostrar melhor: é PERSISTIR. Aqui as ocorrências ficam
 * guardadas, sobrevivem a redesenho, a troca de tela e a reinício do app.
 *
 * Guarda pouco de propósito. Não é telemetria, não sai do aparelho e não tenta
 * ser completo — são as últimas dezenas de ocorrências, o suficiente para
 * responder "o que houve com aquele download de ontem à noite".
 */

export type Nivel = "info" | "erro";

export interface Ocorrencia {
  quando: string;
  nivel: Nivel;
  /** De qual parte veio: download, modelo, geração, visão, busca. */
  origem: string;
  texto: string;
}

const CHAVE = "wuwei.registro.local";
/** Teto de linhas. Passando disso, as mais antigas caem.
 *
 *  Sem teto, um download que falha em laço enche o localStorage e derruba
 *  TUDO que depende dele — sessão inclusive. Um registro de diagnóstico não
 *  pode ser a causa do próximo problema. */
const MAXIMO = 60;

let memoria: Ocorrencia[] | null = null;

function carregar(): Ocorrencia[] {
  if (memoria) return memoria;
  try {
    const cru = localStorage.getItem(CHAVE);
    memoria = cru ? (JSON.parse(cru) as Ocorrencia[]) : [];
  } catch {
    memoria = [];
  }
  return memoria;
}

function gravar(lista: Ocorrencia[]): void {
  memoria = lista;
  try {
    localStorage.setItem(CHAVE, JSON.stringify(lista));
  } catch {
    // Sem persistência o registro vale só para esta sessão — o que ainda é
    // melhor que nada, e é o comportamento certo quando o armazenamento acaba.
  }
}

export function anotar(origem: string, texto: string, nivel: Nivel = "info"): void {
  const lista = carregar();
  lista.push({ quando: new Date().toISOString(), nivel, origem, texto });
  if (lista.length > MAXIMO) lista.splice(0, lista.length - MAXIMO);
  gravar(lista);
}

/** As ocorrências, da mais recente para a mais antiga.
 *
 *  Ordem invertida porque quem abre o registro está procurando o que acabou de
 *  acontecer, não o começo da história. */
export function ler(): Ocorrencia[] {
  return [...carregar()].reverse();
}

export function limpar(): void {
  gravar([]);
}

/** Houve erro desde a última vez que alguém olhou?
 *
 *  Serve para o cabeçalho do registro avisar sem obrigar a abrir. Um registro
 *  que só conta quando se procura não resolve o caso do download que parou
 *  enquanto a pessoa estava em outra tela. */
export function temErroNaoVisto(): boolean {
  const marca = (() => {
    try {
      return localStorage.getItem(CHAVE + ".visto") ?? "";
    } catch {
      return "";
    }
  })();
  return carregar().some((o) => o.nivel === "erro" && o.quando > marca);
}

export function marcarVisto(): void {
  try {
    localStorage.setItem(CHAVE + ".visto", new Date().toISOString());
  } catch {
    // idem
  }
}

declare global {
  interface Window {
    __wuweiRegistro: { ler: typeof ler; anotar: typeof anotar; limpar: typeof limpar };
  }
}

if (typeof window !== "undefined") {
  window.__wuweiRegistro = { ler, anotar, limpar };
}
